/**
 * Phase-aware curriculum assessment (frontend mirror of functions-new/src/shared/curriculumAssessment.ts)
 */

export const TERM_ORDER = ['autumn', 'spring', 'summer'];

export const TERM_LABELS = {
  autumn: 'Autumn term',
  spring: 'Spring term',
  summer: 'Summer term',
};

export const SUBJECTS_BY_KEY_STAGE = {
  EYFS: ['CLL', 'MD', 'PD', 'PSED'],
  KS1: ['Reading', 'Writing', 'Maths', 'Science'],
  KS2: ['Reading', 'Writing', 'Maths', 'Science'],
  KS3: ['English', 'Maths', 'Science'],
  KS4: ['English', 'Maths', 'Science'],
};

export const DESCRIPTOR_OPTIONS = {
  eyfs_areas: [
    { value: '', label: 'Not recorded' },
    { value: 'Emerging', label: 'Emerging' },
    { value: 'Developing', label: 'Developing' },
    { value: 'Secure', label: 'Secure (at expected)' },
  ],
  primary_descriptors: [
    { value: '', label: 'Not recorded' },
    { value: 'Pre-key stage', label: 'Pre-key stage' },
    { value: 'Working Towards', label: 'Working Towards' },
    { value: 'Expected', label: 'Expected' },
    { value: 'Greater Depth', label: 'Greater Depth' },
  ],
  secondary_descriptors: [
    { value: '', label: 'Not recorded' },
    { value: 'Working below', label: 'Working below' },
    { value: 'Working at', label: 'Working at' },
    { value: 'Above', label: 'Above' },
  ],
  secondary_gcse: [
    { value: '', label: 'Not recorded' },
    { value: 'U', label: 'U (ungraded)' },
    ...Array.from({ length: 9 }, (_, i) => {
      const grade = String(9 - i);
      return { value: grade, label: `Grade ${grade}` };
    }),
  ],
};

export const FRAMEWORK_LABELS = {
  eyfs_areas: 'Early Learning Goals (areas)',
  primary_descriptors: 'Teacher curriculum assessment',
  secondary_descriptors: 'Working-at descriptors (KS3)',
  secondary_gcse: 'GCSE grades (KS4)',
};

export const KEY_STAGE_LABELS = {
  EYFS: 'EYFS',
  KS1: 'Key Stage 1',
  KS2: 'Key Stage 2',
  KS3: 'Key Stage 3',
  KS4: 'Key Stage 4',
};

export function parseYearNumber(yearGroup) {
  if (!yearGroup || typeof yearGroup !== 'string') return null;
  const t = yearGroup.trim().toLowerCase();
  if (t === 'reception') return 0;
  const match = t.match(/year\s*(\d+)/i);
  return match ? parseInt(match[1], 10) : null;
}

export function resolveKeyStage(yearGroup, phase) {
  const yearNum = parseYearNumber(yearGroup);
  if (yearNum === 0) return 'EYFS';
  if (yearNum != null && yearNum >= 1 && yearNum <= 2) return 'KS1';
  if (yearNum != null && yearNum >= 3 && yearNum <= 6) return 'KS2';
  if (yearNum != null && yearNum >= 7 && yearNum <= 9) return 'KS3';
  if (yearNum != null && yearNum >= 10) return 'KS4';
  if (phase === 'secondary') return 'KS3';
  return 'KS2';
}

export function frameworkForKeyStage(keyStage) {
  switch (keyStage) {
    case 'EYFS':
      return 'eyfs_areas';
    case 'KS1':
    case 'KS2':
      return 'primary_descriptors';
    case 'KS3':
      return 'secondary_descriptors';
    case 'KS4':
      return 'secondary_gcse';
    default:
      return 'primary_descriptors';
  }
}

export function getSubjectsForKeyStage(keyStage) {
  return SUBJECTS_BY_KEY_STAGE[keyStage] || SUBJECTS_BY_KEY_STAGE.KS2;
}

export function getDescriptorOptions(framework) {
  return DESCRIPTOR_OPTIONS[framework] || DESCRIPTOR_OPTIONS.primary_descriptors;
}

export function getAcademicYearLabel(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth();
  if (month >= 8) return `${year}-${year + 1}`;
  return `${year - 1}-${year}`;
}

export function getCurrentTerm(date = new Date()) {
  const month = date.getMonth();
  if (month >= 8 || month === 0) return 'autumn';
  if (month >= 1 && month <= 3) return 'spring';
  return 'summer';
}

export function isAtExpected(level, framework) {
  if (!level || !String(level).trim()) return false;
  const v = String(level).trim();

  switch (framework) {
    case 'eyfs_areas':
      return v === 'Secure';
    case 'primary_descriptors':
      return v === 'Expected' || v === 'Greater Depth';
    case 'secondary_descriptors':
      return v === 'Working at' || v === 'Above';
    case 'secondary_gcse': {
      if (v === 'U' || v === '') return false;
      const grade = parseInt(v, 10);
      return !Number.isNaN(grade) && grade >= 4;
    }
    default:
      return false;
  }
}

export function emptySubjectTerm(subjects) {
  return Object.fromEntries(subjects.map((s) => [s, '']));
}

export function emptySubjectAttainment(subjects) {
  const empty = emptySubjectTerm(subjects);
  return { autumn: { ...empty }, spring: { ...empty }, summer: { ...empty } };
}

export function expandLegacyTermAttainment(record, keyStage) {
  const subjects = getSubjectsForKeyStage(keyStage);
  const result = emptySubjectAttainment(subjects);
  const legacy = record.termAttainment || {};

  for (const term of TERM_ORDER) {
    const level = legacy[term];
    if (level && String(level).trim()) {
      for (const subject of subjects) {
        result[term][subject] = String(level).trim();
      }
    }
  }
  return result;
}

export function normalizeSubjectAttainment(record, keyStage) {
  const subjects = getSubjectsForKeyStage(keyStage);
  const base = emptySubjectAttainment(subjects);

  if (record?.subjectAttainment) {
    for (const term of TERM_ORDER) {
      const termData = record.subjectAttainment[term] || {};
      for (const subject of subjects) {
        base[term][subject] = termData[subject] ? String(termData[subject]) : '';
      }
    }
    return base;
  }

  if (record?.termAttainment) {
    return expandLegacyTermAttainment(record, keyStage);
  }

  return base;
}

export function isLegacyRecord(record) {
  if (!record) return false;
  if (record.subjectAttainment) {
    const hasSubject = TERM_ORDER.some((term) => {
      const t = record.subjectAttainment?.[term];
      return t && Object.values(t).some((v) => v && String(v).trim());
    });
    if (hasSubject) return false;
  }
  return Boolean(
    record.termAttainment &&
      TERM_ORDER.some((term) => record.termAttainment?.[term] && String(record.termAttainment[term]).trim()),
  );
}

export function getLatestTermSubjects(subjectAttainment, keyStage, asOfTerm) {
  const coreSubjects = getSubjectsForKeyStage(keyStage);
  const cutoff = asOfTerm || getCurrentTerm();
  const cutoffIdx = TERM_ORDER.indexOf(cutoff);

  for (let i = cutoffIdx; i >= 0; i--) {
    const term = TERM_ORDER[i];
    const termData = subjectAttainment[term] || {};
    const hasAny = coreSubjects.some((s) => termData[s] && String(termData[s]).trim());
    if (hasAny) {
      const subjects = {};
      for (const s of coreSubjects) {
        subjects[s] = termData[s] ? String(termData[s]) : '';
      }
      return { term, subjects };
    }
  }
  return { term: null, subjects: {} };
}

export function isAtExpectedOverall(subjectAttainment, keyStage, framework) {
  const fw = framework || frameworkForKeyStage(keyStage);
  const { subjects } = getLatestTermSubjects(subjectAttainment, keyStage);
  const coreSubjects = getSubjectsForKeyStage(keyStage);
  const recorded = coreSubjects.filter((s) => subjects[s] && String(subjects[s]).trim());
  if (recorded.length === 0) return false;
  return recorded.every((s) => isAtExpected(subjects[s], fw));
}

export function computeSubjectPercentageAtExpected(subjectAttainment, keyStage, framework) {
  const fw = framework || frameworkForKeyStage(keyStage);
  const { subjects } = getLatestTermSubjects(subjectAttainment, keyStage);
  const coreSubjects = getSubjectsForKeyStage(keyStage);
  const recorded = coreSubjects.filter((s) => subjects[s] && String(subjects[s]).trim());
  if (recorded.length === 0) return 0;
  const atExpected = recorded.filter((s) => isAtExpected(subjects[s], fw)).length;
  return Math.round((atExpected / recorded.length) * 100);
}

export function buildSubjectSummary(subjectAttainment, keyStage, framework) {
  const fw = framework || frameworkForKeyStage(keyStage);
  const { subjects } = getLatestTermSubjects(subjectAttainment, keyStage);
  return getSubjectsForKeyStage(keyStage)
    .filter((s) => subjects[s] && String(subjects[s]).trim())
    .map((subject) => ({
      subject,
      level: subjects[subject],
      atExpected: isAtExpected(subjects[subject], fw),
    }));
}

export function mapAttainmentRecordToEhcna(record, keyStage) {
  const subjectAttainment = normalizeSubjectAttainment(record, keyStage);
  const ehcnaKeyStage =
    keyStage === 'EYFS'
      ? 'EYFS'
      : keyStage === 'KS1' || keyStage === 'KS2'
        ? 'KS1_2'
        : keyStage === 'KS3'
          ? 'KS3'
          : 'KS4_5';

  const attainmentByTerm = TERM_ORDER.map((term) => ({
    termLabel: TERM_LABELS[term],
    subjects: Object.fromEntries(
      Object.entries(subjectAttainment[term] || {}).filter(([, v]) => v && String(v).trim()),
    ),
  })).filter((row) => Object.keys(row.subjects).length > 0);

  return {
    keyStage: ehcnaKeyStage,
    assessmentSystemNote: FRAMEWORK_LABELS[frameworkForKeyStage(keyStage)],
    attainmentByTerm,
  };
}

export const ALL_YEAR_GROUPS = [
  'Reception',
  'Year 1',
  'Year 2',
  'Year 3',
  'Year 4',
  'Year 5',
  'Year 6',
  'Year 7',
  'Year 8',
  'Year 9',
  'Year 10',
  'Year 11',
];
